import { useEffect, useSyncExternalStore } from 'react';
import { supabase } from './supabase';
import type { Chat } from '@/data/types';

// Follow-up reminders on a customer or lead (Gary, 3 Nov 2026): one list for Leo's "remind me in May" and the ones
// operators add themselves. Shown on the chat's row, in its Info and in Reminders above the chat list. The database
// decides who sees each one (the area's operators, whoever set it, the chat's owner and head office).

export type ReminderType = 'rebooking' | 'follow_up' | 'service_due';

export interface Reminder {
  id: string;
  date: string;
  type: ReminderType;
  note: string | null;
  customerId: string | null;
  leadId: string | null;
  chatId: string | null;
  name: string;
  area: string | null;
  /** Who set it; null when Leo did. */
  setBy: string | null;
}

interface Row {
  id: string;
  reminder_date: string;
  reminder_type: ReminderType;
  note: string | null;
  customer_id: string | null;
  lead_id: string | null;
  conversation_id: string | null;
  created_by: string | null;
  customers: { customer_name: string | null; first_name: string | null; last_name: string | null; company_name: string | null } | null;
  leads: { first_name: string | null; last_name: string | null; company_name: string | null } | null;
  territories: { name: string | null } | null;
  setter: { first_name: string | null; last_name: string | null } | null;
}

const nameOf = (p: { first_name: string | null; last_name: string | null; company_name: string | null } | null): string =>
  p ? [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || p.company_name?.trim() || '' : '';

/** "CleanMyBed Southern Suburbs" reads as "Southern Suburbs". */
const areaName = (n: string | null | undefined): string | null => (n ? n.replace(/^CleanMyBed\s+/i, '').trim() || null : null);

const SELECT = 'id, reminder_date, reminder_type, note, customer_id, lead_id, conversation_id, created_by,'
  + ' customers(customer_name, first_name, last_name, company_name), leads(first_name, last_name, company_name), territories(name),'
  + ' setter:users!customer_reminders_created_by_fkey(first_name, last_name)';

function fromRow(r: Row): Reminder {
  return {
    id: r.id, date: r.reminder_date, type: r.reminder_type, note: r.note,
    customerId: r.customer_id, leadId: r.lead_id, chatId: r.conversation_id,
    name: r.customers?.customer_name?.trim() || nameOf(r.customers) || nameOf(r.leads) || 'A customer',
    area: areaName(r.territories?.name),
    setBy: r.created_by ? [r.setter?.first_name, r.setter?.last_name].filter(Boolean).join(' ') || 'A team member' : null,
  };
}

// One shared list: a change anywhere reloads every screen that shows it.
let list: Reminder[] = [];
let loaded = false;
let sample = false;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();
const emit = () => { for (const l of listeners) l(); };

const SAMPLE: Reminder[] = [
  { id: 's1', date: isoIn(12), type: 'follow_up', note: 'Two beds by end of November', customerId: null, leadId: 'l1', chatId: null, name: 'Vanessa', area: 'Southern Suburbs', setBy: null },
  { id: 's2', date: isoIn(180), type: 'rebooking', note: 'Rebooking: their next clean is due', customerId: 'c1', leadId: null, chatId: null, name: 'Wayne Cowie', area: 'Southern Suburbs', setBy: null },
];

export async function loadReminders(): Promise<void> {
  if (sample) { list = SAMPLE; loaded = true; emit(); return; }
  loading ??= (async () => {
    const { data, error } = await supabase.from('customer_reminders').select(SELECT)
      .eq('status', 'active').order('reminder_date', { ascending: true }).limit(500);
    if (error) throw new Error(error.message);
    list = ((data ?? []) as unknown as Row[]).map(fromRow);
    loaded = true;
    emit();
  })().finally(() => { loading = null; });
  return loading;
}

/** The whole list, loaded once per session and kept fresh after every change. Sample data in the preview. */
export function useReminders(live: boolean): { reminders: Reminder[]; loaded: boolean } {
  const snap = useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => list);
  useEffect(() => {
    if (!live) sample = true;
    if (!loaded) loadReminders().catch(() => { /* the list shows empty; the next change tries again */ });
  }, [live]);
  return { reminders: snap, loaded };
}

/** The open reminder on this chat, its customer or its lead: the soonest one. */
export function reminderFor(reminders: Reminder[], chat: Pick<Chat, 'id' | 'customerId' | 'leadId'>): Reminder | null {
  return reminders.find((r) => r.chatId === chat.id || (!!chat.customerId && r.customerId === chat.customerId)
    || (!!chat.leadId && r.leadId === chat.leadId)) ?? null;
}

export async function addReminder(input: { chat: Pick<Chat, 'id' | 'customerId' | 'leadId'>; date: string; note: string; type: ReminderType; userId: string }): Promise<void> {
  if (sample) {
    list = [...list, { id: `s${Date.now()}`, date: input.date, type: input.type, note: input.note || null, customerId: input.chat.customerId ?? null, leadId: input.chat.leadId ?? null, chatId: input.chat.id, name: 'This customer', area: null, setBy: 'You' }]
      .sort((a, b) => a.date.localeCompare(b.date));
    emit();
    return;
  }
  const { error } = await supabase.from('customer_reminders').insert({
    customer_id: input.chat.customerId ?? null, lead_id: input.chat.leadId ?? null,
    conversation_id: input.chat.id || null, reminder_date: input.date, reminder_type: input.type,
    note: input.note.trim() || null, created_by: input.userId,
  });
  if (error) throw new Error(friendly(error.message));
  await loadReminders();
}

export async function changeReminder(id: string, change: { date: string; note: string }): Promise<void> {
  if (sample) { list = list.map((r) => (r.id === id ? { ...r, date: change.date, note: change.note || null } : r)); emit(); return; }
  const { data, error } = await supabase.from('customer_reminders')
    .update({ reminder_date: change.date, note: change.note.trim() || null }).eq('id', id).select('id');
  if (error) throw new Error(friendly(error.message));
  if (!data?.length) throw new Error('Only the area\'s team or head office can change this reminder.');
  await loadReminders();
}

/** Done (followed up) or cancelled (not needed any more). Both leave the list; Sentinel keeps the record. */
export async function closeReminder(id: string, status: 'done' | 'cancelled'): Promise<void> {
  if (sample) { list = list.filter((r) => r.id !== id); emit(); return; }
  const { data, error } = await supabase.from('customer_reminders').update({ status }).eq('id', id).select('id');
  if (error) throw new Error(friendly(error.message));
  if (!data?.length) throw new Error('Only the area\'s team or head office can change this reminder.');
  await loadReminders();
}

function friendly(message: string): string {
  if (/row-level security|permission|42501/i.test(message)) return 'You cannot set a reminder on this customer. Ask head office.';
  return 'That did not save. Check your signal and try again.';
}

/** Today as YYYY-MM-DD on this device. */
export function todayIso(): string {
  return isoIn(0);
}

function isoIn(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** The same day so many months on (31 Jan + 1 month is the last day of February). */
export function monthsOn(months: number): string {
  const d = new Date();
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "1 May" this year, "1 May 2027" otherwise. */
export function shortDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}

export const TYPE_WORDS: Record<ReminderType, string> = { rebooking: 'Rebooking', follow_up: 'Follow up', service_due: 'Service due' };
